import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Warehouse, MagnifyingGlass, MapPin, Barcode } from "@phosphor-icons/react";
import LocationPicker from "@/components/inventory/LocationPicker";
import LocationDetail from "@/components/inventory/LocationDetail";
import SkuLookup from "@/components/inventory/SkuLookup";
import { locationLookup } from "@/lib/inventoryApi";

/**
 * Warehouse inventory page — admin/manager only (gated at the router).
 *
 * Layout:
 *   • Header with a free-form location code input + "Pick from list" button.
 *   • Live detail view of the currently-selected location — pulls from
 *     GET /api/inventory/location/{code} every time the code changes so
 *     stock counts stay fresh.
 *   • Selected code is mirrored to the URL query (?loc=...) so ops can
 *     share a link to a specific bin.
 *
 * The `?loc=` param acts as the source of truth for the current selection
 * so refresh/back-button behave correctly.
 */
export default function Inventory() {
  const [params, setParams] = useSearchParams();
  const urlLoc = (params.get("loc") || "").trim();
  const urlTab = params.get("tab") === "sku" ? "sku" : "location";

  const [input, setInput] = useState(urlLoc);
  const [activeCode, setActiveCode] = useState(urlLoc);
  const [tab, setTab] = useState(urlTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep the URL in sync with the active code + tab (single source of truth).
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (activeCode) next.set("loc", activeCode);
    else next.delete("loc");
    if (tab === "sku") next.set("tab", "sku");
    else next.delete("tab");
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCode, tab]);

  const doLookup = useCallback(async (code) => {
    if (!code) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await locationLookup(code);
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load location");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { doLookup(activeCode); }, [activeCode, doLookup]);

  const submit = useCallback((e) => {
    e?.preventDefault?.();
    const code = (input || "").trim();
    setActiveCode(code);
  }, [input]);

  const pickFromList = useCallback((code) => {
    setInput(code);
    setActiveCode(code);
  }, []);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <header>
        <div className="flex items-center gap-2">
          <Warehouse size={20} weight="bold" className="text-zinc-500" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
            Warehouse
          </div>
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Inventory</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
          Browse warehouse bins and see what's on hand. Data pulled live from Base44 —
          treated as the source of truth for stock counts.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-sm grid-cols-2" data-testid="inventory-tabs">
          <TabsTrigger value="location" data-testid="tab-location" className="gap-1.5">
            <MapPin size={14} weight="bold" /> By Location
          </TabsTrigger>
          <TabsTrigger value="sku" data-testid="tab-sku" className="gap-1.5">
            <Barcode size={14} weight="bold" /> By SKU
          </TabsTrigger>
        </TabsList>

        <TabsContent value="location" className="mt-4 space-y-4">
          <Card className="p-4">
            <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[220px]">
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1 block">
                  Location code
                </label>
                <div className="relative">
                  <MagnifyingGlass
                    size={16}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"
                  />
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="e.g. RACK1-SHELF1 or A-01-05"
                    className="pl-8 h-10 font-mono"
                    data-testid="inventory-code-input"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2 pb-[2px]">
                <LocationPicker
                  value={activeCode}
                  onChange={pickFromList}
                  testId="inventory-picker"
                />
                <Button
                  type="submit"
                  className="bg-[#C8102E] hover:bg-[#A00D25] h-10"
                  data-testid="inventory-lookup-submit"
                >
                  Look up
                </Button>
              </div>
            </form>
          </Card>

          {activeCode ? (
            <LocationDetail
              data={data}
              loading={loading}
              error={error}
              onRetry={() => doLookup(activeCode)}
            />
          ) : (
            <Card className="p-8 text-center">
              <Warehouse size={40} weight="duotone" className="mx-auto text-zinc-300" />
              <div className="mt-3 text-sm text-zinc-500">
                Enter a location code above or click <strong>Pick from list</strong> to browse bins.
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sku" className="mt-4">
          <SkuLookup />
        </TabsContent>
      </Tabs>
    </div>
  );
}
